import { memo, useContext } from 'react';
import { Breadcrumbs } from 'src/sensors/Breadcrumbs';
import { MESSAGES } from 'src/modules/common/constants';
import { RuntimeContext } from 'src/runtimeContext';
import { PATHS } from 'src/sensors/common/types/Paths';
import { LocationState } from 'src/sensors/LocationState';
import { BREADCRUMB_STATUS } from 'src/common/types/BreadcrumbStatus.ts';
import { BreadcrumbContainer } from 'src/common/Breadcrumb/BreadcrumbContainer.tsx';
import { removeFromBreadcrumb, setFromBreadcrumb } from 'src/common/breadcrumbUtils.ts';

const SENSOR_BREADCRUMB_PAGES = {
    [PATHS.Sensors]: 0,
    [PATHS.Accessories]: 1,
    [PATHS.Checkout]: 2,
};

const getPageStatus = (
    comparing: keyof typeof SENSOR_BREADCRUMB_PAGES,
    comparedTo: keyof typeof SENSOR_BREADCRUMB_PAGES,
) => {
    if (SENSOR_BREADCRUMB_PAGES[comparing] === SENSOR_BREADCRUMB_PAGES[comparedTo]) {
        return BREADCRUMB_STATUS.PROGRESS;
    }

    if (SENSOR_BREADCRUMB_PAGES[comparing] < SENSOR_BREADCRUMB_PAGES[comparedTo]) {
        return BREADCRUMB_STATUS.EMPTY;
    }

    return BREADCRUMB_STATUS.DONE;
};

type Props = {
    currentPage: keyof typeof SENSOR_BREADCRUMB_PAGES;
    locationState: LocationState;
    searchParams: URLSearchParams;
    resetHandler?: () => void;
}

export const SensorBreadcrumbs = memo(({ currentPage, locationState, searchParams, resetHandler }: Props) => {
    const { isMobile } = useContext(RuntimeContext);

    const isAccessoriesDisabled = !locationState.sensorLabel;

    const searchParamsForBreadcrumbs = removeFromBreadcrumb(searchParams);
    const searchParamsForAccessories = setFromBreadcrumb(searchParams);

    const breadcrumbProps: Breadcrumbs = [
        {
            title: MESSAGES.SelectedSensor,
            value: locationState.sensorLabel || '-',
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Sensors),
            linkState: locationState,
            onActionClick: resetHandler,
            to: {
                pathname: PATHS.Sensors,
                search: searchParamsForBreadcrumbs.toString(),
            },
        },
        {
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Accessories),
            title: MESSAGES.SelectedAccessories,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.map(({ label }) => label).join(', ') : '-',
            disabled: isAccessoriesDisabled,
            linkState: locationState,
            onActionClick: resetHandler,
            to: {
                pathname: PATHS.Accessories,
                search: searchParamsForAccessories.toString(),
            },
        },
        {
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Checkout),
            title: MESSAGES.checkoutSensorsBreadcrumbTitle,
            value: '',
            disabled: isAccessoriesDisabled,
            linkState: locationState,
            onActionClick: resetHandler,
            to: {
                pathname: PATHS.Checkout,
                search: searchParamsForBreadcrumbs.toString(),
            },
        },
    ];

    return (
        <BreadcrumbContainer
            isMobile={isMobile}
            breadcrumbs={breadcrumbProps}
            containerWrapperClass="vss-sensors-breadcrumbs"
        />
    );
});
